import type { SortingState } from '@tanstack/react-table'
import { useState } from 'react'

function isSortingState(value: unknown): value is SortingState {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        entry !== null &&
        typeof entry === 'object' &&
        typeof (entry as { id?: unknown }).id === 'string' &&
        typeof (entry as { desc?: unknown }).desc === 'boolean',
    )
  )
}

/**
 * Persists the user's table sort order to localStorage under `storageKey`. Falls back to
 * `defaultSorting` when nothing valid is stored.
 */
export function useTableSorting(
  storageKey: string,
  defaultSorting: SortingState = [],
): {
  sorting: SortingState
  onSortingChange: (nextSorting: SortingState) => void
} {
  const [sorting, setSorting] = useState<SortingState>(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed: unknown = JSON.parse(stored)
        if (isSortingState(parsed)) return parsed
      }
    } catch {
      // corrupt JSON or storage unavailable — fall through to default
    }
    return defaultSorting
  })

  function onSortingChange(nextSorting: SortingState) {
    setSorting(nextSorting)
    try {
      localStorage.setItem(storageKey, JSON.stringify(nextSorting))
    } catch {
      // storage may be unavailable (private mode, quota); sorting still works in-memory
    }
  }

  return { sorting, onSortingChange }
}
